import { Logger, emptyObject, mergeDeep } from '@sitecore-search/common';
import { getDefaultEntityId } from './entityUtils.js';
import EventContext from './eventContext.js';
import { buildEventEntities, buildReviewObject, buildTransactionObject, track, validateEntities, validateReview, validateTransaction, } from './eventUtils.js';
/**
 * @desc Sent when the user adds to cart an entity, delegating the responsibility to trackAddEvent
 * @param entity
 * @param payload
 * @returns  {Promise<*>}
 */
export const trackAddToCartEvent = async (entity, { items, actionSubtype = 'conversion' }) => {
    return trackAddEvent(entity, 'cart', { actionSubtype, items });
};
/**
 * @desc Sent when the user adds an entity to the wishlist, delegating the responsibility to trackAddEvent
 * @param entity
 * @param payload
 * @returns  {Promise<*>}
 */
export const trackAddToWishlistEvent = async (entity, payload) => {
    return trackAddEvent(entity, 'wishlist', payload);
};
/**
 * @desc Sent when the user confirms the order, delegating the responsibility to trackAddEvent
 * @param entity
 * @param payload
 * @returns  {Promise<*>}
 */
export const trackOrderConfirmEvent = async (entity, payload) => {
    const { orderId, orderTotal, orderSubtotal, coupon, items, actionSubtype = 'conversion', store } = payload;
    if (!validateTransaction(orderId)) {
        Logger.warn('trackEvent :::', 'Transaction orderId is not defined');
        return;
    }
    return trackAddEvent(entity, 'order_confirm', {
        items,
        transaction: buildTransactionObject(orderId, orderTotal, orderSubtotal, coupon, store),
        actionSubtype,
    });
};
/**
 * @desc Sent when the user made a review, delegating the responsibility to trackAddEvent
 * @param entity
 * @param payload
 * @returns  {Promise<*>}
 */
export const trackAddReview = async (entity, payload) => {
    const { id, rating, comment, items, actionSubtype } = payload;
    if (!validateReview(id)) {
        Logger.warn('trackEvent :::', 'Review id is not defined');
        return;
    }
    return trackAddEvent(entity, 'review', {
        review: buildReviewObject(id, rating, comment),
        items,
        actionSubtype,
    });
};
/**
 * @desc Sent when user add an entity
 * @param entity
 * @param name
 * @param payload
 * @returns {Promise<*>}
 */
export const trackAddEvent = async (entity, name, { review, transaction, items, customAttributes, actionSubtype }) => {
    if (!name) {
        Logger.warn('trackEvent :::', 'Event name is not defined');
        return;
    }
    let eventEntities = [];
    if (entity && items) {
        eventEntities = buildEventEntities(entity, items);
    }
    const params = {
        name,
        action: 'add',
        action_sub_type: actionSubtype,
        value: {
            entities: eventEntities,
            transaction,
            review,
            custom_attributes: customAttributes,
            context: EventContext.toJson(),
        },
    };
    return track(params);
};
/**
 * @desc Sent when user remove an entity
 * @param entity
 * @param name
 * @param payload
 * @returns {Promise<*>}
 */
export const trackRemoveEvent = async (entity, name, { actionSubtype, review, items }) => {
    if (!name) {
        Logger.warn('trackEvent :::', 'Event name is not defined');
        return;
    }
    let eventEntities = [];
    if (entity && items) {
        eventEntities = buildEventEntities(entity, items);
        if (eventEntities && !validateEntities(eventEntities)) {
            Logger.warn('trackEvent :::', 'A field is missing on one or more of the items provided');
            return;
        }
    }
    const params = {
        name,
        action: 'remove',
        action_sub_type: actionSubtype,
        value: {
            entities: eventEntities,
            review,
            context: EventContext.toJson(),
        },
    };
    return track(params);
};
/**
 * @desc Page view event. Visitor views any page. Examples: confirm, cart, category, search, detail, qview
 * @param name
 * @returns {Promise<*>}
 */
export const trackPageViewEvent = async (name, { actionSubtype } = {}) => {
    if (!name) {
        Logger.warn('trackEvent :::', 'Event name is not defined');
        return;
    }
    const params = {
        action: 'view',
        name,
        action_sub_type: actionSubtype,
        value: {
            context: EventContext.toJson(),
        },
    };
    return track(params);
};
/**
 * @desc Entity Page view event
 * @param entity
 * @param items
 * @returns {Promise<*>}
 */
export const trackEntityPageViewEvent = async (entity, { items, actionSubtype } = {}) => {
    const context = EventContext.toJson();
    let eventEntities;
    if (items) {
        eventEntities = buildEventEntities(entity, items);
        if (eventEntities && !validateEntities(eventEntities)) {
            Logger.warn('trackEvent :::', 'A field is missing on one or more of the items provided');
            return;
        }
    }
    else {
        eventEntities = buildEventEntities(entity, [
            {
                id: getDefaultEntityId(context),
            },
        ]);
    }
    const params = {
        action: 'view',
        name: 'entity_page',
        action_sub_type: actionSubtype,
        value: {
            entities: eventEntities,
            context,
        },
    };
    return track(params);
};
/**
 * @desc Tracks cart status
 * @param entity
 * @param payload
 * @returns {Promise<*>}
 */
export const trackStatusCartEvent = async (entity, { items, actionSubtype }) => {
    let eventEntities = [];
    if (entity && items) {
        eventEntities = buildEventEntities(entity, items);
        if (eventEntities && !validateEntities(eventEntities)) {
            Logger.warn('trackEvent :::', 'A field is missing on one or more of the items provided');
            return;
        }
    }
    const params = {
        action: 'view',
        name: 'cart',
        action_sub_type: actionSubtype,
        value: {
            entities: eventEntities,
            context: EventContext.toJson(),
        },
    };
    return track(params);
};
/**
 * @desc
 * This event must be sent when a user logs in to the system.
 * This event is used in linking real user id to a Reflektion cookie so it would help
 * in more advanced user personalization use cases like cross device personalization
 * @param userModel id & email
 * @param actionSubtype
 * @returns {Promise<*>}
 */
export const trackUserLoginEvent = async (userModel, actionSubtype) => {
    if (!userModel || emptyObject(userModel)) {
        Logger.warn('trackEvent :::', 'User information is empty or not complete');
        return;
    }
    const contextValue = mergeDeep(EventContext.toJson(), {
        user: {
            ...(userModel.id ? { cuid: userModel.id } : {}),
            ...(userModel.eid ? { eid: userModel.eid } : {}),
            ...(userModel.email ? { email: userModel.email } : {}),
            ...(userModel.fbid ? { fbid: userModel.fbid } : {}),
            ...(userModel.address ? { address: userModel.address } : {}),
        },
    });
    const params = {
        action: 'identify',
        name: 'login',
        action_sub_type: actionSubtype,
        value: {
            context: contextValue,
        },
    };
    return track(params);
};
